import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Globe, Users, Music, Target, Building2, Mail } from "lucide-react";
import { Footer } from "../components/Footer";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8cb5d32c-b1ce-4951-b77e-897f98e6cc9f/artifacts/uyctypm7_41ed1e03-8bc9-42a0-8314-b43b52fec9a9.JPG";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About Us - Afro Fanthem";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Afro Fanthem" className="h-10 md:h-14 w-auto" />
            </Link>
            <div className="flex items-center gap-3 sm:gap-6">
              <Link to="/" className="text-gray-300 hover:text-white transition-colors font-medium text-sm sm:text-base">
                Home
              </Link>
              <Link to="/forum" className="text-gray-300 hover:text-white transition-colors font-medium text-sm sm:text-base hidden sm:inline">
                Forum
              </Link>
              <Link
                to="/survey"
                className="bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-4 sm:px-6 py-2 sm:py-3 text-sm hover:bg-[#F59E0B] transition-all"
                data-testid="about-survey-button"
              >
                Take the Survey
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-28 md:pt-36 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <img src={LOGO_URL} alt="Afro Fanthem" className="h-24 md:h-32 w-auto mx-auto mb-8" />
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white uppercase tracking-tight mb-6" data-testid="about-headline">
            About <span className="text-[#FBBF24]">Afro Fanthem</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Afro Fanthem is a digital platform and music project created to celebrate, count, and amplify African football fans around the world.
          </p>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="py-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">Why We Exist</h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-15">
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                We believe African supporters are central to global football culture, but their voices are often underrepresented in how clubs, leagues, media, and brands understand fandom.
              </p>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Afro Fanthem exists to validate and prove that African football fandom has a real place in the world. African fans do not simply watch football from a distance. They sing, debate, stream, travel, buy, organise, influence, and pass club loyalty across families, cities, countries, and generations.
              </p>
            </div>
          </div>

          <div className="py-12 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center flex-shrink-0">
                <Target className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">Our Purpose</h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-15">
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                The purpose of Afro Fanthem is to make that passion visible. Through surveys, fan stories, music, chants, anthems, and community discussion, Afro Fanthem is building a stronger evidence base for the recognition of African supporters.
              </p>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Afro Fanthem exists because African football fandom deserves to be seen, measured, respected, and represented. The project is driven by the need to validate and prove to the world that African fandom has a real place in global football culture.
              </p>
            </div>
          </div>

          <div className="py-12 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center flex-shrink-0">
                <Globe className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">What We Do</h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-15">
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                We use music, surveys, fan stories, and community discussion to amplify African football fans and build evidence that clubs, leagues, media, and brands cannot ignore.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-white/5 border border-white/10 p-5">
                  <Music className="w-5 h-5 text-[#10B981] mb-3" />
                  <h3 className="text-white font-bold mb-2">Music & Anthems</h3>
                  <p className="text-gray-400 text-sm">Club anthems, chants, and tracks that celebrate African football culture.</p>
                </div>
                <div className="bg-white/5 border border-white/10 p-5">
                  <Users className="w-5 h-5 text-[#3B82F6] mb-3" />
                  <h3 className="text-white font-bold mb-2">Fan Survey</h3>
                  <p className="text-gray-400 text-sm">Collecting responses to build the African Football Fan Report.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="py-12 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">Who We Are</h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-15">
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Afro Fanthem is associated with and operated by <strong className="text-white">Southern African Traders Ltd</strong>, a UK registered company listed at Companies House under company number <strong className="text-white">12003857</strong>.
              </p>
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                The company provides the structure and accountability behind the project.
              </p>
            </div>
          </div>

          <div className="py-12 border-t border-white/10">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FBBF24]/10 flex items-center justify-center flex-shrink-0">
                <Mail className="w-6 h-6 text-[#FBBF24]" />
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl text-white uppercase tracking-tight">Contact</h2>
            </div>
            <div className="space-y-4 pl-0 md:pl-15">
              <p className="text-gray-300 text-base sm:text-lg leading-relaxed">
                Whether you are a fan, a club, a media outlet, a brand, or a fan group, we would like to hear from you.
              </p>
              <div className="space-y-3 mt-4">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <span className="text-gray-400 text-sm">Afro Fanthem enquiries: </span>
                    <a href="mailto:afrofanthem@afrofanthem.com" className="text-[#FBBF24] hover:underline">afrofanthem@afrofanthem.com</a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-[#FBBF24]" />
                  <div>
                    <span className="text-gray-400 text-sm">Company/admin enquiries: </span>
                    <a href="mailto:admin@satraders.co.uk" className="text-[#FBBF24] hover:underline">admin@satraders.co.uk</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#141414] border-t border-white/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight mb-6" data-testid="about-cta-headline">
            Join the <span className="text-[#FBBF24]">Movement</span>
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed max-w-2xl mx-auto mb-10">
            Take the African Football Fan Survey and help prove the scale, passion, and influence of African football fans. Your voice matters.
          </p>
          <Link
            to="/survey"
            className="inline-flex items-center justify-center gap-2 bg-[#FBBF24] text-black font-bold uppercase tracking-widest px-10 py-5 text-lg hover:bg-[#F59E0B] transition-all hover:-translate-y-1"
            data-testid="about-join-button"
          >
            Take the Survey
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
